import { Link } from "react-router-dom";
import { Calculator, CheckCircle, Clock, Globe, Shield, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

export function HeroSection() {
  const { t } = useLanguage();

  const badges = [
    { icon: CheckCircle, textKey: "hero.badge_clients" },
    { icon: Globe, textKey: "hero.badge_countries" },
    { icon: Clock, textKey: "hero.badge_express" },
  ];

  return (
    <section className="relative py-20 md:py-28 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[hsl(var(--hero-gradient-from))] to-[hsl(var(--hero-gradient-to))]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-48 h-48 bg-secondary rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-primary-foreground rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50" />

      <div className="container mx-auto px-4 relative z-10 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 bg-secondary/20 backdrop-blur-sm px-4 py-2 rounded-full text-secondary mb-8">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t("hero.trust_badge", "11 rokov skúseností • 180+ Google referencií")}
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-primary-foreground leading-tight mb-6">
            {t("hero.title_line1", "Legalizácia dokumentov a úradné preklady.")}
            <br />
            <span className="text-secondary">{t("hero.title_line2", "Profesionálne. Spoľahlivo.")}</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
            {t(
              "hero.subtitle",
              "Apostila, superlegalizácia a úradné preklady. Kompletný servis pre súkromné osoby aj firmy.",
            )}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold px-8 py-6 text-lg rounded-xl shadow-2xl hover:shadow-secondary/25 transition-all hover:scale-105"
            >
              <Link to="/kalkulator">
                <Calculator className="w-5 h-5 mr-2" />
                {t("hero.cta_calculator", "Bezplatná cenová ponuka")}
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white/30 hover:bg-white/10 text-white font-bold px-8 py-6 text-lg rounded-xl backdrop-blur-sm"
            >
              <a href="tel:0907999909">
                <Phone className="w-5 h-5 mr-2" />
                0907 999 909
              </a>
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {badges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-primary-foreground/80">
                <badge.icon className="w-5 h-5 text-secondary" />
                <span className="text-sm font-medium">{t(badge.textKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
